import { createBrowserRouter } from 'react-router-dom';
import { stringify } from 'qs';

import { router } from '@/App';
import { FrontPopupErrorStatus } from '@/constants/error/frontErrorStatus';
import {
  ServerAuthErrorStatus,
  ServerPageErrorStatus,
  ServerPopupErrorStatus
} from '@/constants/error/serverErrorStatus';
import { ROUTES } from '@/constants/routes';
import { useAlertPopupStore } from '@/store/alertPopup';
import { getParamsFromRoute, replacePathParams } from '@/utils/route';

type Router = ReturnType<typeof createBrowserRouter>;

export const handleServerPageError = (router: Router, status: ServerPageErrorStatus) => {
  switch (status) {
    case ServerPageErrorStatus.FORBIDDEN: {
      router.navigate(ROUTES.ERROR.NOT_PARTNER, { replace: true });
      break;
    }

    default: {
      router.navigate(ROUTES.ERROR.INTERNAL, { replace: true, state: { returnUrl: location.href } });
      break;
    }
  }
};

export const handlePopupError = (status: ServerPopupErrorStatus | FrontPopupErrorStatus) => {
  const { openErrorPopup } = useAlertPopupStore.getState();

  switch (status) {
    case ServerPopupErrorStatus.BAD_REQUEST:
    case ServerPopupErrorStatus.BAD_PARAMETER: {
      openErrorPopup({ bodyText: '입력된 내용을 확인해 주세요.' });
      break;
    }

    case ServerPopupErrorStatus.BIZ_TYPE_NOT_MATCHED: {
      openErrorPopup({ bodyText: '비즈타입이 맞지 않습니다.' });
      break;
    }

    case ServerPopupErrorStatus.LAST_DEALER_PERMISSION_USER: {
      openErrorPopup({
        bodyText: '관리자 1명은 필수입니다.\n다른 관리자 1명을 먼저 추가하고 변경 및 삭제해 주세요.'
      });
      break;
    }

    case ServerPopupErrorStatus.LAST_PARTNER_PERMISSION_USER: {
      openErrorPopup({
        bodyText: '관리자 1명은 필수입니다.\n다른 관리자 1명을 먼저 추가하고 변경 및 삭제해 주세요.'
      });
      break;
    }

    case ServerPopupErrorStatus.DORMANT_USER: {
      openErrorPopup({
        headerText: '초대 메일 발송 불가',
        bodyText: '휴면 계정입니다.\n휴면 해제 후 다시 초대해 주세요.'
      });
      break;
    }

    case ServerPopupErrorStatus.INVALID_STATUS_USER: {
      openErrorPopup({
        headerText: '초대 메일 발송 불가',
        bodyText: '사용할 수 없는 계정입니다.'
      });
      break;
    }

    case ServerPopupErrorStatus.DUPLICATED_CONTRACT_APPLIED_AT:
    case ServerPopupErrorStatus.INVALID_CONTRACT_APPLIED_AT: {
      openErrorPopup({
        headerText: '계약 추가 불가',
        bodyText:
          '이미 등록된 동일한 상품과 계약기간이 중복되거나 계약연장설정이 ‘자동’으로 설정되어 수동으로 계약을\n추가할 수 없습니다.\n확인 후 다시 시도해 주세요.'
      });
      break;
    }

    case FrontPopupErrorStatus.CI_DOC_EXCEEDED_FILE_COUNT:
    case ServerPopupErrorStatus.CI_DOC_EXCEEDED_FILE_COUNT: {
      openErrorPopup({ bodyText: '등록 가능한 파일은 최대 5개 입니다.' });
      break;
    }

    case FrontPopupErrorStatus.CI_DOC_NOT_ALLOWED_FILE_FORMAT:
    case ServerPopupErrorStatus.CI_DOC_NOT_ALLOWED_FILE_FORMAT: {
      openErrorPopup({ bodyText: 'PDF, JPG, PNG, GIF 파일 형식만 등록할 수 있습니다.' });
      break;
    }

    case FrontPopupErrorStatus.CI_DOC_EXCEEDED_FILE_SIZE:
    case ServerPopupErrorStatus.CI_DOC_EXCEEDED_FILE_SIZE: {
      openErrorPopup({
        bodyText: '등록 가능한 파일 크기는 최대 15MB입니다.'
      });
      break;
    }

    case FrontPopupErrorStatus.APP_ICON_EXCEEDED_FILE_SIZE:
    case ServerPopupErrorStatus.APP_ICON_EXCEEDED_FILE_SIZE: {
      openErrorPopup({
        bodyText: '등록 가능한 이미지 크기는 최대 250KB입니다.'
      });
      break;
    }

    case FrontPopupErrorStatus.APP_ICON_NOT_ALLOWED_FILE_FORMAT:
    case ServerPopupErrorStatus.APP_ICON_NOT_ALLOWED_FILE_FORMAT: {
      openErrorPopup({
        bodyText: 'JPG, GIF, PNG 파일 형식만 등록할 수 있습니다.'
      });
      break;
    }

    case FrontPopupErrorStatus.SETTLE_ADJUST_REQUEST_EXCEEDED_FILE_COUNT:
    case ServerPopupErrorStatus.SETTLE_ADJUST_REQUEST_EXCEEDED_FILE_COUNT: {
      openErrorPopup({ bodyText: '등록 가능한 파일은 최대 5개 입니다.' });
      break;
    }

    case FrontPopupErrorStatus.SETTLE_ADJUST_REQUEST_NOT_ALLOWED_FILE_FORMAT:
    case ServerPopupErrorStatus.SETTLE_ADJUST_REQUEST_NOT_ALLOWED_FILE_FORMAT: {
      openErrorPopup({ bodyText: 'JPG, PNG, XLSX, CSV 파일 형식만 등록할 수 있습니다.' });
      break;
    }

    case FrontPopupErrorStatus.SETTLE_ADJUST_REQUEST_EXCEEDED_FILE_SIZE:
    case ServerPopupErrorStatus.SETTLE_ADJUST_REQUEST_EXCEEDED_FILE_SIZE: {
      openErrorPopup({
        bodyText: '등록 가능한 파일 크기는 최대 15MB입니다.'
      });
      break;
    }

    case ServerPopupErrorStatus.NOT_PUBLIC_IPS: {
      openErrorPopup({
        headerText: 'IP 등록 불가',
        bodyText: '입력한 IP의 형식이나 공인IP 여부를 확인해 주세요.\n(사설 IP 등록 불가)'
      });
      break;
    }

    case ServerPopupErrorStatus.NOT_VALID_EMAIL: {
      openErrorPopup({
        bodyText: '이메일 주소를 확인해 주세요.'
      });
      break;
    }

    case ServerPopupErrorStatus.NOT_VALID_PHONE_NUMBER: {
      openErrorPopup({
        bodyText: "전화번호에 '-' 를 포함해 주세요."
      });
      break;
    }

    case ServerPopupErrorStatus.NOT_ENOUGH_PERMISSION: {
      openErrorPopup({ bodyText: '해당 유저를 삭제할 권한이 없습니다.' });
      break;
    }

    case ServerPopupErrorStatus.NOT_FOUNT_USER_BY_EMAIL: {
      openErrorPopup({
        headerText: '초대 메일 발송 불가',
        bodyText: '유효하지 않은 카카오계정입니다.'
      });
      break;
    }

    case ServerPopupErrorStatus.USER_ALREADY_INVITED: {
      openErrorPopup({ bodyText: '이미 초대중인 계정입니다.' });
      break;
    }

    case ServerPopupErrorStatus.USER_ALREADY_EXIST:
    case ServerPopupErrorStatus.CAN_NOT_CANCEL_INVITATION_REGISTERED_USER: {
      openErrorPopup({ bodyText: '이미 초대된 멤버입니다.' });
      break;
    }

    case ServerPopupErrorStatus.USER_ALREADY_EXIST_WITH_DIFFERENT_EMAIL: {
      openErrorPopup({
        bodyText: '이 카카오계정의 다른 이메일로 가입이 완료되어 초대가 불가합니다.\n다른 카카오계정으로 초대해 주세요.'
      });
      break;
    }

    case ServerPopupErrorStatus.FAIL_TO_SET_ALIM_TALK: {
      openErrorPopup({
        headerText: '이용기관 채널 확인 필요',
        bodyText: '채널관리자센터에서 고객센터 전화번호 추가가 필요합니다.'
      });
      break;
    }

    case ServerPopupErrorStatus.SVC_NAME_NOT_USABLE: {
      openErrorPopup({
        bodyText: '이 서비스에 동일한 기관명이 존재하여 사용할 수 없습니다.'
      });
      break;
    }

    case ServerPopupErrorStatus.KAPI_CREATE_OWNERAPP: {
      openErrorPopup({
        headerText: '앱 생성 실패',
        bodyText: '일시적으로 앱을 생성할 수 없습니다.\n잠시 후 다시 시도해 주세요.'
      });
      break;
    }

    case ServerPopupErrorStatus.KAPI_EXCEEDED_APP_COUNT: {
      openErrorPopup({
        headerText: '앱 생성 실패',
        bodyText:
          '보유 가능한 앱 최대 개수(OWNER 10개)를 초과하여 앱을 생성할 수 없습니다.\n디벨로퍼스에서 앱 삭제 후 다시 시도해 주세요.'
      });
      break;
    }

    case ServerPopupErrorStatus.BAD_ALIM_TALK_PARAMETER:
    case ServerPopupErrorStatus.BAD_CS_CENTER_PARAMETER:
    case ServerPopupErrorStatus.ONLY_HAS_ONE_SVC:
    case ServerPopupErrorStatus.INCOMPLETE_SVC:
    case ServerPopupErrorStatus.ALREADY_HAS_APP:
    case ServerPopupErrorStatus.ONLY_HAS_ONE_REVIEW:
    case ServerPopupErrorStatus.NOT_FOUND_DEALER:
    case ServerPopupErrorStatus.NOT_FOUND_PARTNER:
    case ServerPopupErrorStatus.NOT_FOUND_SVC:
    case ServerPopupErrorStatus.NOT_FOUND_SETTLE:
    case ServerPopupErrorStatus.NOT_FOUND_CONTRACT:
    case ServerPopupErrorStatus.FAIL_FILE_UPLOAD:
    case ServerPopupErrorStatus.FAIL_EXPIRE_HANDLING:
    case ServerPopupErrorStatus.ERROR_TENTH_API:
    case ServerPopupErrorStatus.KAPI_GET_APP:
    case ServerPopupErrorStatus.KAPI_GET_APP_LIST:
    case ServerPopupErrorStatus.KAPI_GET_DEVELOPER:
    case ServerPopupErrorStatus.KAPI_USING_KAKAO_LOGIN:
    case ServerPopupErrorStatus.KAPI_IS_BIZAPP:
    case ServerPopupErrorStatus.KAPI_GET_REST_KEY:
    case ServerPopupErrorStatus.NOT_FOUND_PRODUCT:
    case ServerPopupErrorStatus.INCOMPLETE_APP_INFO:
    case ServerPopupErrorStatus.INVALID_CONTRACT:
    case ServerPopupErrorStatus.INVALID_CHARGING_PERIOD_CONTRACT: {
      openErrorPopup({
        bodyText: `일시적인 오류가 발생했습니다.(${status})\n오류가 지속되면 카카오 담당자에게 문의해 주세요.`
      });
      break;
    }

    case ServerPopupErrorStatus.NOT_VALID_CHANNEL: {
      openErrorPopup({ bodyText: '비즈채널이 아니거나 등록되지 않은 채널입니다.' });
      break;
    }

    case ServerPopupErrorStatus.SERVER_ERROR: {
      openErrorPopup({ bodyText: '일시적인 문제가 발생했습니다.' });
      break;
    }

    case ServerPopupErrorStatus.MODIFY_OWN_PERMISSION: {
      openErrorPopup({ bodyText: '본인의 권한은 수정할 수 없습니다.' });
      break;
    }

    case ServerPopupErrorStatus.LAST_PERMISSION_USER: {
      openErrorPopup({
        bodyText:
          '이용 중인 계약이 있는 경우, 딜러사 관리자는 탈퇴가 불가합니다.\n다른 관리자를 등록하셔야 탈퇴할 수 있습니다.'
      });
      break;
    }

    case ServerPopupErrorStatus.VALID_DEALER_CONTRACT_NOT_FOUND: {
      openErrorPopup({
        bodyText: '딜러사의 계약기간이 유효하지 않습니다.\n딜러사의 계약 상태를 확인해 주세요.'
      });
      break;
    }

    case FrontPopupErrorStatus.SVC_LOGO_EXCEEDED_FILE_SIZE:
    case ServerPopupErrorStatus.SVC_LOGO_EXCEEDED_FILE_SIZE: {
      openErrorPopup({
        bodyText: '등록 가능한 이미지 크기는 최대 250KB입니다.'
      });
      break;
    }

    case ServerPopupErrorStatus.SVC_LOGO_NOT_ALLOWED_IMAGE_SIZE: {
      openErrorPopup({
        bodyText: '130X130 사이즈만\n등록할 수 있습니다.'
      });
      break;
    }

    case FrontPopupErrorStatus.SVC_LOGO_NOT_ALLOWED_FILE_FORMAT:
    case ServerPopupErrorStatus.SVC_LOGO_NOT_ALLOWED_FILE_FORMAT: {
      openErrorPopup({
        bodyText: 'JPG, PNG 파일 형식만\n등록할 수 있습니다.'
      });
      break;
    }

    case ServerPopupErrorStatus.NOT_MODIFY_READY_CONTRACT: {
      openErrorPopup({
        bodyText: '이용대기중인 계약의 이용상태는 변경할 수 없습니다.'
      });
      break;
    }

    case ServerPopupErrorStatus.ONLY_MODIFY_READY_CONTRACT: {
      openErrorPopup({
        bodyText: '이용중인 계약에서 수정할 수 없는 항목이 포함되어 있습니다.'
      });
      break;
    }

    case ServerPopupErrorStatus.NOT_MODIFY_FINISH_CONTRACT: {
      openErrorPopup({
        bodyText: '만료된 계약의 정보는 수정할 수 없습니다.'
      });
      break;
    }

    case ServerPopupErrorStatus.AUTO_RENEWAL_ALREADY_EXIST: {
      openErrorPopup({
        bodyText: '동일한 상품이 등록되어 있습니다. 이미 등록된 상품의 계약연장설정을 변경하고 다시 시도해주세요.'
      });
      break;
    }

    case ServerPopupErrorStatus.LAST_PARTNER_PERMISSION_USER_WITHDRAW: {
      openErrorPopup({
        bodyText:
          '이용 중인 계약이 있는 경우, 최후의 관리자 1명은 탈퇴가 불가합니다.\n다른 관리자를 등록하셔야 탈퇴할 수 있습니다.'
      });
      break;
    }

    case FrontPopupErrorStatus.INVALID_EMAIL: {
      openErrorPopup({
        bodyText: '이메일 주소를 확인해 주세요.'
      });
      break;
    }

    case FrontPopupErrorStatus.DUPLICATED_EMAIL: {
      openErrorPopup({
        bodyText: '중복된 이메일 주소가 있습니다.\n이메일 주소를 확인해 주세요.'
      });
      break;
    }

    case FrontPopupErrorStatus.INVITED_ACCOUNT_NOT_MATCHED: {
      openErrorPopup({
        bodyText:
          '초대받은 계정과 다른 계정으로 카카오 비즈니스 서비스에 로그인한 상태입니다.\n로그아웃 후 초대 메일의 초대 수락 버튼을 클릭하시면, 초대받은 계정으로 가입할 수 있습니다.'
      });
      break;
    }

    case FrontPopupErrorStatus.BUSINESS_NUMBER_NOT_VALIDATED: {
      openErrorPopup({
        headerText: '사업자등록번호 조회 필요',
        bodyText: '사업자등록번호를 입력하고 조회 버튼을 클릭해 주세요.'
      });
      break;
    }

    case FrontPopupErrorStatus.INVALID_BUSINESS_NUMBER: {
      openErrorPopup({
        bodyText: '사용할 수 없는 사업자등록번호입니다. 다시 한번 확인해 주세요.'
      });
      break;
    }

    case FrontPopupErrorStatus.MANAGER_EMAIL_NOT_VALIDATED: {
      openErrorPopup({
        headerText: '관리자 이메일 확인 필요',
        bodyText: '관리자 이메일을 입력하고 확인 버튼을 클릭해 주세요.'
      });
      break;
    }

    case FrontPopupErrorStatus.SVC_NAME_NOT_VALIDATED: {
      openErrorPopup({
        headerText: '기관명 확인 필요',
        bodyText: `'조회' 버튼을 클릭하여 기관명 등록 가능 여부를 확인해 주세요.`
      });
      break;
    }

    case FrontPopupErrorStatus.PARTNER_CHANNEL_URL_NOT_VALIDATED: {
      openErrorPopup({
        headerText: '이용기관 채널 확인 필요',
        bodyText: '채널URL을 입력하고 확인 버튼을 클릭해 주세요.'
      });
      break;
    }

    case FrontPopupErrorStatus.PARTNER_CHANNEL_UUID_REQUIRED: {
      openErrorPopup({
        headerText: '알림톡 이미지 등록 불가',
        bodyText:
          "알림톡 이미지를 등록하려면 이용기관 채널 입력이 필요합니다. 채널 URL을 입력하고 '확인' 버튼을 클릭해 주세요."
      });
      break;
    }

    case ServerPopupErrorStatus.CI_DOC_FILE_REQUIRED:
    case FrontPopupErrorStatus.CI_DOC_FILE_REQUIRED: {
      openErrorPopup({
        headerText: '증빙자료 첨부 필요',
        bodyText: '이용기관의 CI보유 여부 증빙자료를 첨부해 주세요.'
      });
      break;
    }

    case ServerPopupErrorStatus.MISMATCHED_CI_PERMISSION_CHANGE_WITH_SETTLES: {
      openErrorPopup({
        headerText: 'CI 활용여부 변경',
        bodyText:
          "기관의 CI 활용여부는 모든 Settle 정보의 CI 활용여부가 '활용안함'이어야 수정할 수 있습니다. 수정 후 다시 시도해 주세요."
      });
      break;
    }

    case ServerPopupErrorStatus.FAILED_SETTLE_MAIL: {
      openErrorPopup({
        bodyText:
          'Settle 생성이 완료되었으나 연동담당자에게 이메일이 발송되지 않았습니다. 잠시 후 이메일 재전송 버튼을 클릭해 주세요.'
      });
      break;
    }

    case ServerPopupErrorStatus.FAILED_SETTLE_DELETE: {
      openErrorPopup({
        bodyText: '등록된 계약이 있어 Settle을 삭제할 수 없습니다.'
      });
      break;
    }

    case FrontPopupErrorStatus.ALIM_TALK_IMAGE_EXCEEDED_FILE_SIZE:
    case ServerPopupErrorStatus.ALIM_TALK_IMAGE_EXCEEDED_FILE_SIZE: {
      openErrorPopup({
        bodyText: '등록 가능한 이미지 크기는 최대 500KB입니다.'
      });
      break;
    }

    case FrontPopupErrorStatus.ALIM_TALK_IMAGE_NOT_ALLOWED_FILE_FORMAT:
    case ServerPopupErrorStatus.ALIM_TALK_IMAGE_NOT_ALLOWED_FILE_FORMAT: {
      openErrorPopup({
        bodyText: 'JPG, PNG 파일형식만 등록할 수 있습니다.'
      });
      break;
    }

    case ServerPopupErrorStatus.ALIM_TALK_IMAGE_NOT_ALLOWED_IMAGE_SIZE: {
      openErrorPopup({ bodyText: '800X400 사이즈만 등록할 수 있습니다.' });
      break;
    }

    case ServerPopupErrorStatus.ILLEGAL_FILMING_IMAGE: {
      openErrorPopup({
        bodyText: '전기통신사업법에 따라 불법촬영물등에 해당하여 이미지를 등록할 수 없습니다.'
      });
      break;
    }

    case ServerPopupErrorStatus.FAIL_ILLEGAL_FILMING_IMAGE_CHECK: {
      openErrorPopup({
        bodyText: '일시적인 오류로 이미지 검사를 완료하지 못했습니다.\n잠시 후 다시 첨부해 주세요.'
      });
      break;
    }

    case ServerPopupErrorStatus.CAN_NOT_DELETE_SVC_PARTNER_HAS_ONE_SVC: {
      openErrorPopup({
        bodyText:
          '이용기관에 등록된 유일한 기관은 삭제할 수 없습니다.\n신규 기관 추가 후 해당 기관 정보를 삭제하거나,\n카카오 담당자에게 문의해 주세요.'
      });
      break;
    }

    case ServerPopupErrorStatus.CAN_NOT_DELETE_SVC_ALREADY_REGISTERED_SETTLE: {
      openErrorPopup({ bodyText: '등록된 Settle이 있어 기관 정보를 삭제할 수 없습니다.' });
      break;
    }

    case ServerPopupErrorStatus.NOT_ELIGIBLE_FOR_SETTLEMENT: {
      openErrorPopup({
        bodyText: '정산 대상이 아닙니다.',
        onConfirm: () => {
          const { AUTH_INFO_ID } = getParamsFromRoute(ROUTES.SETTLE.BASE_INFO);
          router.navigate(replacePathParams(ROUTES.DASHBOARD, { AUTH_INFO_ID }));
        }
      });
      break;
    }

    case ServerPopupErrorStatus.NO_AVAILABLE_OPERATING_SETTLE: {
      openErrorPopup({
        bodyText: '등록된 발행 정보가 없습니다.\n발행 정보는 운영용 Settle과 함께 등록됩니다.',
        onConfirm: () => {
          const { AUTH_INFO_ID } = getParamsFromRoute(ROUTES.SETTLE.BASE_INFO);
          router.navigate(replacePathParams(ROUTES.DASHBOARD, { AUTH_INFO_ID }));
        }
      });
      break;
    }

    case ServerPopupErrorStatus.INVALID_INVITE_MAIL: {
      openErrorPopup({
        headerText: '메일 발송 실패',
        bodyText: '입력한 메일이 수신 가능한 상태인지 확인해 주세요. (저장 용량, 수신 거부, 휴면 상태 등 확인)'
      });
      break;
    }

    default: {
      openErrorPopup({
        bodyText: '일시적인 오류가 발생했습니다.\n잠시 후 다시 시도해 주세요.'
      });
    }
  }
};

export const handleServerAuthError = (router: Router, status: ServerAuthErrorStatus) => {
  const { openErrorPopup } = useAlertPopupStore.getState();

  switch (status) {
    case ServerAuthErrorStatus.UNAUTHORIZED:
      location.href = `${ACCOUNT_URL}?continue=${encodeURIComponent(location.href)}`;
      break;
    case ServerAuthErrorStatus.NOT_CRUX_USER:
      location.href = `${KAKAO_BUSINESS_URL}/account/join/new-service?${stringify({
        after: location.href,
        from: 'WALLET_PARTNER',
        showSvcAgreements: true
      })}`;
      break;
    case ServerAuthErrorStatus.NOT_PC_USER:
      location.href = `${KAKAO_BUSINESS_URL}/account/join/new-agreement?${stringify({
        after: location.href,
        from: 'WALLET_PARTNER',
        showSvcAgreements: true
      })}`;
      break;
    case ServerAuthErrorStatus.NOT_FOUNT_USER:
      location.href = `${KAKAO_BUSINESS_URL}/account/auth?${stringify({
        after: location.href,
        from: 'WALLET_PARTNER'
      })}`;
      break;
    case ServerAuthErrorStatus.UNAUTHORIZED_AUTH_INFO_ID:
      router.navigate(ROUTES.ROOT, { replace: true });
      break;

    default:
      openErrorPopup({
        bodyText: (
          <>
            일시적인 오류가 발생했습니다.
            <br />
            잠시 후 다시 시도해 주세요.
          </>
        )
      });
  }
};
